import {
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  InputBase,
  MenuItem,
  Modal,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, typography } from "@mui/system";
import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import {
  getImportBillData,
  getImportBillById,
  searchImportItem,
} from "../../../services/Import";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import {
  deleteSubOrderById,
  getSubOrderByPaging,
} from "../../../services/SubOrder";
import SubPersonSelectingPopup from "../../../components/SubPersonSelectingPopup";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import AddSubOrder from "./AddSubOrder";
import ConfirmationPopup from "../../../components/ConfirmationPopup";
import { RoleBasedRender } from "../../../components/RoleBaseRender";
import SubOrderReportDialog from "./SubOrderReportDialog";
import AssessmentIcon from "@mui/icons-material/Assessment";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

// Order workflow status — independent of payment progress (see paymentStatusOptions).
export const statusOptions = [
  { value: "", label: "" },
  { value: "DRAFT", label: "Draft" },
  { value: "AWAITING_APPROVAL", label: "Awaiting Approval" },
  { value: "REJECTED", label: "Rejected" },
  { value: "AWAITING_FOR_SUPPLY_ITEMS", label: "Awaiting For Supply Items" },
  { value: "PROCESSING", label: "Order Processing" },
  { value: "COMPLETED", label: "Completed" },
];

// Payment status — tracked separately from order workflow status; derived from paidAmount vs cost.
export const paymentStatusOptions = [
  { value: "AWAITING_PAYMENT", label: "Awaiting Payment" },
  { value: "PARTIALLY_PAID", label: "Partially Paid" },
  { value: "PAID", label: "Paid" },
];

// Persists across unmount/remount without JSON serialization
let _subOrderListState = null;

function SubOrder() {
  const history = useHistory();

  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openForm, setOpenForm] = React.useState(false);
  const [orderLoading, setOrderLoading] = React.useState(false);
  const [itemLoading, set_itemLoading] = React.useState(false);
  const [totalPages, setTotalPages] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [openInputItemTable, setOpenInputItemTable] = useState(false);
  const [selectedInputItems, setSelectedInputItems] = useState([]);
  const [openOutputItemTable, setOpenOutputItemTable] = useState(false);
  const [selectedOutputItems, setSelectedOutputItems] = useState([]);
  const [itemRows, setItemRows] = useState([]);
  const [openSubOrderPersonTable, setOpenSubOrderPersonTable] = useState(false);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [personRows, setPersonRows] = useState([]);

  // Confirmation state for delete action
  const [isMatched, setIsMatched] = useState(false);
  const [matchingText, setMachingText] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [deletingRow, setDeletingRow] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [openReportDialog, setOpenReportDialog] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        if (_subOrderListState) {
          const { page: savedPage, rowsPerPage: savedSize, filter: savedFilter } = _subOrderListState;
          _subOrderListState = null;
          setPage(savedPage);
          setRowsPerPage(savedSize);
          setFilter(savedFilter);
          await fetchData(savedPage, savedFilter, savedSize);
        } else {
          await fetchData(0);
        }
      } catch (err) {
        if (isMounted) console.error("Failed to load sub orders:", err);
        if (isMounted) await fetchData(0);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  // Effect for deletion confirmation
  useEffect(() => {
    if (isMatched && !openPopup && deletingRow !== null) {
      handleDeleteOrder(matchingText);
    }
  }, [isMatched, openPopup]);

  //get order data
  const fetchData = async (pageNumber = 0, filterOverride = null, pageSizeOverride = null) => {
    setOrderLoading(true);
    const activeFilter = filterOverride ?? filter;
    const activePageSize = pageSizeOverride ?? rowsPerPage;
    let result;
    try {
      result = await getSubOrderByPaging(pageNumber, activePageSize, activeFilter);
    } catch (err) {
      setOrderLoading(false);
      return;
    }
    if (!result || typeof result !== "object" || !Array.isArray(result.content)) {
      setOrderLoading(false);
      return;
    }
    let orderSet = result.content;

    //set total rows and pages
    setTotalPages(result.totalPages);
    setTotalRows(result?.totalElements || orderSet?.length || 0);

    const newSet = [];
    if (orderSet?.length !== 0) {
      for (let x = 0; x < orderSet?.length; x++) {
        const rawDate = orderSet[x].orderDate;
        let splitDate = rawDate && typeof rawDate === "string" ? rawDate.split("T") : [""];
        const outputLineItems = orderSet[x].lineItems
          ? orderSet[x].lineItems.filter(
              (item) => item.lineItemType === "OUTPUT"
            )
          : [];
        const statusLabel =
          statusOptions.find((opt) => opt.value === orderSet[x].status)
            ?.label || orderSet[x].status;

        newSet.push(
          createDataForOrders(
            splitDate[0],
            orderSet[x].orderId,
            orderSet[x].subOrderPerson.personName,
            statusLabel,
            renderPaymentStatus(orderSet[x].paymentStatus),
            renderOutputItems(outputLineItems),
            orderSet[x].orderId
          )
        );
      }
    }
    setOrderLoading(false);

    // set rows to table
    setRows(newSet);
  };

  // Builds the "Output Item(s)" cell — a chip per OUTPUT line item, showing the
  // concrete item name when it was added directly, or the category name when
  // the line item is a category placeholder. The icon/color distinguish the two.
  function renderOutputItems(outputLineItems) {
    if (!outputLineItems || outputLineItems.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
        {outputLineItems.map((item) => {
          const isCategory = item.importCategory != null;
          const label = isCategory
            ? item.importCategory?.category
            : item.imports?.itemName;

          return (
            <Chip
              key={item.id}
              size="small"
              icon={isCategory ? <CategoryIcon /> : <Inventory2Icon />}
              label={label || "Unknown"}
              variant={isCategory ? "outlined" : "filled"}
              color={isCategory ? "secondary" : "primary"}
              title={isCategory ? "Category" : "Item"}
            />
          );
        })}
      </Stack>
    );
  }

  // Renders the "Payment Status" chip — independent of the order's workflow status.
  function renderPaymentStatus(paymentStatus) {
    const option = paymentStatusOptions.find((opt) => opt.value === paymentStatus);
    const colorByStatus = {
      AWAITING_PAYMENT: "default",
      PARTIALLY_PAID: "warning",
      PAID: "success",
    };
    return (
      <Chip
        size="small"
        label={option?.label || paymentStatus || "-"}
        color={colorByStatus[paymentStatus] || "default"}
        variant={paymentStatus === "PAID" ? "filled" : "outlined"}
      />
    );
  }

  //creating data for rows according to Id
  function createDataForOrders(
    date,
    orderNumber,
    orderPerson,
    status,
    paymentStatus,
    outputItems,
    action,
    orderId
  ) {
    return {
      date,
      orderNumber,
      orderPerson,
      status,
      paymentStatus,
      outputItems,
      action,
      orderId,
    };
  }

  //columns for table
  const columns_order = [
    { id: "date", label: "Date", minWidth: 100 },
    { id: "orderNumber", label: "Order Number", minWidth: 100 },
    { id: "orderPerson", label: "Order Person", minWidth: 100 },
    { id: "status", label: "Status", minWidth: 100 },
    { id: "paymentStatus", label: "Payment Status", minWidth: 130 },
    { id: "outputItems", label: "Output Item(s)", minWidth: 220 },
    { id: "action", label: "Actions", minWidth: 100 },
  ];

  //Drop down list for table
  const popUpList = [
    { id: 1, label: "View Items" },
    { id: 2, label: "Edit" },
    { id: 3, label: "Delete" },
  ];

  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(value - 1);
  };

  // filter values
  const [filter, setFilter] = useState({
    _subOrderPersonIds: [],
    _inputImportIds: [],
    _outputImportIds: [],
    _status: "",
    _orderId: "",
  });

  //reset filters
  const resetFilters = () => {
    setSelectedPersons([]);
    setSelectedInputItems([]);
    setSelectedOutputItems([]);
    const emptyFilter = {
      _subOrderPersonIds: [],
      _inputImportIds: [],
      _outputImportIds: [],
      _status: "",
      _orderId: "",
    };
    setFilter(emptyFilter);
    setPage(0);
    fetchData(0, emptyFilter);
  };

  //to handle changing filters
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleAction = (event, id) => {
    switch (event) {
      case "view":
        _subOrderListState = { page, rowsPerPage, filter };
        history.push("/template/sub_order_view/" + id);
        break;
      case "edit":
        history.push("/template/im_bill_edit/" + id);
        break;
      case "delete":
        handleDeleteConfirmation(id);
        break;
      case "return":
        history.push("/template/im_bill_return/" + id);
        break;
      default:
        return "foo";
    }
  };

  // input Item popup handlers
  const handleOpenInputItemTable = async () => {
    setOpenInputItemTable(true);
  };

  const handleCloseInputItemTable = async () => {
    setOpenInputItemTable(false);
  };

  // output Item popup handlers
  const handleOpenOutputItemTable = async () => {
    setOpenOutputItemTable(true);
  };

  const handleCloseOutputItemTable = async () => {
    setOpenOutputItemTable(false);
  };

  // Sub Order Person popup handlers
  const handleOpenSubOrderPersonTable = async () => {
    setOpenSubOrderPersonTable(true);
  };

  const handleCloseSubOrderPersonTable = async () => {
    setOpenSubOrderPersonTable(false);
  };

  // Handle delete confirmation
  const handleDeleteConfirmation = (id) => {
    setIsMatched(false);
    let filterData = rows.find((row) => row.action === id);
    setDeletingRow(filterData);
    let topicText = `Please confirm bill number to delete: ${filterData.action}`;
    setConfirmationText(topicText);
    setMachingText(filterData.action);
    setOpenPopup(true);
  };

  const handleDeleteOrder = async (orderId) => {
    setOrderLoading(true);
    let deleteOrderResponse = await deleteSubOrderById(orderId);
    if (deleteOrderResponse) {
      fetchData();
      setOrderLoading(false);
    } else {
      setOrderLoading(false);
      console.error("Failed to delete order");
    }
  };

  // Confirmation popup handler
  const handleOpenPopup = () => {
    setOpenPopup(!openPopup);
  };

  return (
    <StyledPaper elevation={0}>
      {openForm ? (
        <AddSubOrder setOpenForm={setOpenForm} />
      ) : (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ReceiptIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  Sub Order Management
                </Typography>
                <Typography variant="body2">
                  View and manage Sub Orders and their items.
                </Typography>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={1.5}>
                  <RoleBasedRender
                    allowedRoles={["SUB_ORDER_VIEW", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<AssessmentIcon />}
                      onClick={() => setOpenReportDialog(true)}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.15)",
                        borderColor: "rgba(255,255,255,0.6)",
                        color: "inherit",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.25)",
                          borderColor: "white",
                        },
                      }}
                    >
                      Generate Report
                    </ActionButton>
                  </RoleBasedRender>
                  <RoleBasedRender
                    allowedRoles={["SUB_ORDER_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenForm(true)}
                    >
                      Add Order
                    </ActionButton>
                  </RoleBasedRender>
                </Stack>
              </Grid>
            </Grid>
          </HeaderCard>

          <Box
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2}>
              <Tooltip title="Toggle Filters">
                <ActionButton
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={toggleFilters}
                >
                  Filters
                </ActionButton>
              </Tooltip>
              <Chip
                label={`${totalRows} Categories`}
                variant="outlined"
                color="primary"
              />
            </Stack>
          </Box>

          {showFilters && (
            <FilterContainer>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3} md={3}>
                  <SelectButton
                    startIcon={<PersonIcon />}
                    onClick={handleOpenSubOrderPersonTable}
                    endIcon={
                      selectedPersons.length > 0 ? (
                        <Chip size="small" label={selectedPersons.length} />
                      ) : null
                    }
                  >
                    {selectedPersons.length > 0
                      ? "Selected Persons"
                      : "Select Persons"}
                  </SelectButton>
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <SelectButton
                    startIcon={<InventoryIcon />}
                    onClick={handleOpenInputItemTable}
                    endIcon={
                      selectedInputItems.length > 0 ? (
                        <Chip size="small" label={selectedInputItems.length} />
                      ) : null
                    }
                  >
                    {selectedInputItems.length > 0
                      ? "Input Items Selected"
                      : "Select Input Items"}
                  </SelectButton>
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <SelectButton
                    startIcon={<InventoryIcon />}
                    onClick={handleOpenOutputItemTable}
                    endIcon={
                      selectedOutputItems.length > 0 ? (
                        <Chip size="small" label={selectedOutputItems.length} />
                      ) : null
                    }
                  >
                    {selectedOutputItems.length > 0
                      ? "Output Items Selected"
                      : "Select Output Items"}
                  </SelectButton>
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    value={filter._status}
                    onChange={(e) =>
                      setFilter({ ...filter, _status: e.target.value })
                    }
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {statusOptions
                      .filter((opt) => opt.value !== "")
                      .map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="Order Number"
                    value={filter._orderId}
                    onChange={(e) =>
                      setFilter({ ...filter, _orderId: e.target.value })
                    }
                    onWheel={(e) => e.target.blur()}
                  />
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Reset Filters">
                    <ActionButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={resetFilters}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Apply Filters">
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        const activeFilter = {
                          _subOrderPersonIds: selectedPersons,
                          _inputImportIds: selectedInputItems,
                          _outputImportIds: selectedOutputItems,
                          _status: filter._status,
                          _orderId: filter._orderId,
                        };
                        setFilter(activeFilter);
                        setPage(0);
                        fetchData(0, activeFilter);
                      }}
                    >
                      Apply Filters
                    </ActionButton>
                  </Tooltip>
                </Stack>
              </Box>
            </FilterContainer>
          )}

          <Divider sx={{ my: 2 }} />

          {orderLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 5 }}>
              <Typography variant="body1" color="textSecondary">
                No Sub Orders found.
              </Typography>
            </Box>
          ) : (
            <TableItem
              setRow={null}
              dropDown={false}
              columns={columns_order}
              rows={rows}
              handleAction={handleAction}
              showActions={{
                view: {
                  label: "View Order",
                },
                delete: {
                  label: "Delete Order",
                  condition: (row) =>
                    row.status === "Draft" || row.status === "Rejected",
                },
              }}
              actionRoles={{
                view: ["SUB_ORDER_VIEW", "ROLE_ADMIN"],
                delete: ["SUB_ORDER_DELETE", "ROLE_ADMIN"],
              }}
            />
          )}
          <PaginationContainer>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Rows per page:
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => {
                  const newSize = e.target.value;
                  setRowsPerPage(newSize);
                  setPage(0);
                  fetchData(0, filter, newSize);
                }}
              >
                {[5, 10, 25, 50].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              )}
            </Stack>
          </PaginationContainer>
        </>
      )}
      {openSubOrderPersonTable ? (
        <SubPersonSelectingPopup
          setOpenPersonTable={setOpenSubOrderPersonTable}
          openPersonTable={openSubOrderPersonTable}
          handleClosePersonTable={handleCloseSubOrderPersonTable}
          setSelectedPerson={setSelectedPersons}
          selectedPerson={selectedPersons}
        />
      ) : null}

      {openInputItemTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenInputItemTable}
          openTable={openInputItemTable}
          handleCloseTable={handleCloseInputItemTable}
          setSelectedItems={setSelectedInputItems}
          selectedItems={selectedInputItems}
          setRows={setItemRows}
          rows={itemRows}
        />
      )}
      {openOutputItemTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenOutputItemTable}
          openTable={openOutputItemTable}
          handleCloseTable={handleCloseOutputItemTable}
          setSelectedItems={setSelectedOutputItems}
          selectedItems={selectedOutputItems}
          setRows={setItemRows}
          rows={itemRows}
        />
      )}
      {openPopup && (
        <ConfirmationPopup
          setOpenPopup={setOpenPopup}
          openPopup={openPopup}
          handleOpenPopup={handleOpenPopup}
          matchingText={matchingText}
          message={confirmationText}
          isMatched={isMatched}
          setIsMatched={setIsMatched}
        />
      )}
      {openReportDialog && (
        <SubOrderReportDialog
          open={openReportDialog}
          onClose={() => setOpenReportDialog(false)}
        />
      )}
    </StyledPaper>
  );
}

export default SubOrder;
